<script>
import BaseModal from '@/components/_base-modal.vue'
import { mapActions } from 'vuex'
import VuetifyColourClass from '@/shared/constants/core/VuetifyColourClass'
import baseMixin from '@/mixins/baseMixin'
import dateMixin from '@/mixins/dateMixin'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import { getTodaysDateString } from '../../helpers/date-helpers'

export default {
  name: 'CandidateExportDialog',
  components: {
    BaseModal,
  },
  mixins: [baseMixin, dateMixin],
  props: {
    clientIds: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    show: false,
    isLoading: false,
    startDate: null,
    endDate: null,
    showStartPicker: false,
    showEndPicker: false,
    greyText: VuetifyColourClass.text.grey600,
  }),
  computed: {
    formattedStartDate() {
      return this.startDate
        ? this.formatDate(this.startDate, DateFormatToken.DoMMMYYYY)
        : ''
    },
    formattedEndDate() {
      return this.endDate
        ? this.formatDate(this.endDate, DateFormatToken.DoMMMYYYY)
        : ''
    },
    isExportDisabled() {
      return this.isLoading || !this.startDate || !this.endDate
    },
    maxStartDate() {
      return this.endDate || getTodaysDateString()
    },
    maxEndDate() {
      return getTodaysDateString()
    },
  },
  methods: {
    ...mapActions({
      exportCandidates: 'candidate/exportCandidates',
    }),
    open() {
      this.show = true
    },
    close() {
      this.show = false
    },
    async handleExport() {
      this.isLoading = true

      const exportData = await this.exportCandidates({
        clientIds: this.clientIds,
        startDate: this.startDate,
        endDate: this.endDate,
      })

      if (!exportData) {
        this.isLoading = false
        return
      }

      this.downloadJson(exportData)
      this.isLoading = false
      this.show = false
    },
    downloadJson(data) {
      const formattedStart = this.formatDate(
        this.startDate,
        DateFormatToken.DoMMMYYYY
      )
      const formattedEnd = this.formatDate(
        this.endDate,
        DateFormatToken.DoMMMYYYY
      )
      const filename = `EducatorExport(${formattedStart} - ${formattedEnd}).json`

      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.click()
      setTimeout(() => URL.revokeObjectURL(url), 100)
    },
  },
}
</script>

<template>
  <div>
    <v-btn
      outlined
      class="export-button py-5"
      :loading="isLoading"
      :disabled="isLoading"
      :height="!isMobile ? '' : '42'"
      @click="open"
    >
      <v-icon :class="{ 'mr-4 mt-1': !isMobile }" size="20"
        >mdi-download</v-icon
      >
      {{ !isMobile ? $t('candidateExport.exportButton') : '' }}
    </v-btn>

    <BaseModal
      v-model="show"
      :title="$t('candidateExport.title')"
      hide-header-divider
      width="600px"
    >
      <p
        v-sanitized-html="$t('candidateExport.description')"
        :class="greyText"
        class="mb-6"
      />
      <v-row>
        <v-col cols="4">
          <v-menu
            v-model="showStartPicker"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">
                <v-text-field
                  :value="formattedStartDate"
                  :label="$t('candidateExport.startDateLabel')"
                  readonly
                  outlined
                  dense
                  hide-details
                  prepend-inner-icon="mdi-calendar"
                  class="pointer-events-none"
                />
              </div>
            </template>
            <v-date-picker
              v-model="startDate"
              no-title
              :max="maxStartDate"
              @input="showStartPicker = false"
            />
          </v-menu>
        </v-col>

        <v-col cols="4">
          <v-menu
            v-model="showEndPicker"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">
                <v-text-field
                  :value="formattedEndDate"
                  :label="$t('candidateExport.endDateLabel')"
                  readonly
                  outlined
                  dense
                  hide-details
                  prepend-inner-icon="mdi-calendar"
                  class="pointer-events-none"
                />
              </div>
            </template>
            <v-date-picker
              v-model="endDate"
              no-title
              :min="startDate"
              :max="maxEndDate"
              @input="showEndPicker = false"
            />
          </v-menu>
        </v-col>
      </v-row>

      <template v-slot:actions>
        <BaseButton
          sm
          color="primary"
          :loading="isLoading"
          :disabled="isExportDisabled"
          @click="handleExport"
        >
          {{ $t('candidateExport.exportButton') }}
        </BaseButton>
      </template>
    </BaseModal>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/_colors.scss';

.export-button {
  font-weight: 500;
  color: $color-gray-600;
  text-transform: none;
  border-color: $color-neutral-grey-200;
  border-radius: 8px;

  ::v-deep .v-icon {
    color: $color-neutral-grey-400;
  }

  &:hover {
    color: $color-blue-500 !important;
    background-color: $color-info-270 !important;
    border-color: $color-info-300 !important;

    ::v-deep .v-icon {
      color: $color-blue-400 !important;
    }
  }
}
</style>
